import { useCallback } from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import { isNil } from 'lodash-es';

import { IStore } from '@rbi-ctg/store';
import { GetRestaurantDocument, IGetRestaurantQuery } from 'generated/rbi-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { ServiceMode } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { Store, StoreState } from 'state/store/hooks';
import { isMobileOrderingAvailable, isRestaurantOpen } from 'utils/restaurant';

export type ServiceModeStatus = {
  [key in ServiceMode]: {
    /**
     * store is available in MDM
     */
    capable: boolean;
    /**
     * store is available if store hours are within service mode availability
     * and the store supports this service mode type
     */
    available: boolean;
    /**
     * state is disabled based on flag from LD, separate from store's availability
     */
    disabled: boolean;
  };
};

export const useServiceModeStatus = (store: StoreState | Store) => {
  const { query } = useApolloClient();
  const logger = useLogger();
  const { serviceMode } = useServiceModeContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);

  // Move these up in order to check for null (meaning LD service is down) or undefined
  // FYI - use ENABLE_* flags to avoid this necessity as null values will correctly disable the option
  const driveThruDisabled = useFlag(LaunchDarklyFlag.DISABLE_DRIVE_THRU);
  const dineInDisabled = useFlag(LaunchDarklyFlag.DISABLE_DINE_IN);
  const takeOutDisabled = useFlag(LaunchDarklyFlag.DISABLE_TAKE_OUT);

  const mdmStore = (store !== null ? store : {}) as IStore;

  const serviceModeStatus = {
    CATERING_DELIVERY: {
      capable: !!mdmStore.hasDelivery && !!mdmStore.hasCatering,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CATERING_DELIVERY)
    },
    CATERING_PICKUP: {
      capable: !!mdmStore.hasCatering,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CATERING)
    },
    CURBSIDE: {
      capable: !!mdmStore.hasCurbside,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CURBSIDE)
    },
    DELIVERY: {
      capable: !!mdmStore.hasDelivery,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_DELIVERY)
    },
    DRIVE_THRU: {
      capable: !!mdmStore.hasDriveThru,
      available: false,
      disabled: isNil(driveThruDisabled) || driveThruDisabled
    },
    EAT_IN: {
      capable: !!mdmStore.hasDineIn,
      available: false,
      disabled: isNil(dineInDisabled) || dineInDisabled
    },
    TAKEOUT: {
      capable: !!mdmStore.hasTakeOut,
      available: false,
      disabled: isNil(takeOutDisabled) || takeOutDisabled
    }
  };

  let allServiceModesUnavailable = true;

  // performing an explicit check against `false` because some stores do not have these properties configured
  // therefore hasSelection and isRestaurantPosOnline can be undefined even though the store is available
  if (
    store !== null &&
    (!('hasSelection' in store) || ('hasSelection' in store && store.hasSelection !== false))
  ) {
    serviceModeStatus.CATERING_DELIVERY.available = serviceModeStatus.CATERING_DELIVERY.capable;
    serviceModeStatus.CATERING_PICKUP.available = serviceModeStatus.CATERING_PICKUP.capable;
    serviceModeStatus.DELIVERY.available =
      serviceModeStatus.DELIVERY.capable && isRestaurantOpen(store.deliveryHours);
    serviceModeStatus.EAT_IN.available =
      serviceModeStatus.EAT_IN.capable && isRestaurantOpen(store.diningRoomHours);
    serviceModeStatus.CURBSIDE.available =
      serviceModeStatus.CURBSIDE.capable && isRestaurantOpen(store.curbsideHours);
    serviceModeStatus.TAKEOUT.available =
      serviceModeStatus.TAKEOUT.capable && isRestaurantOpen(store.diningRoomHours);
    serviceModeStatus.DRIVE_THRU.available =
      serviceModeStatus.DRIVE_THRU.capable && isRestaurantOpen(store.driveThruHours);

    allServiceModesUnavailable = Object.values(serviceModeStatus).every(
      ({ available, disabled }) => !available || disabled
    );
  }

  const checkIfRestaurantPosIsOnline = useCallback(
    /* eslint-disable-next-line */
    async (storeId: string) => {
      try {
        if (!storeId) {
          return false;
        }

        if (!enableOrdering) {
          return true;
        }

        const { data, errors } = await query<IGetRestaurantQuery>({
          query: GetRestaurantDocument,
          variables: { storeId },
          fetchPolicy: 'network-only'
        });
        // optimistically return true if this fails due to a network error
        return errors ? true : data?.restaurant?.available;
      } catch (error) {
        logger.warn({
          error,
          storeId,
          message: 'Failed to retrieve restaurant availability'
        });
        // optimistically return true if the query throws
        return true;
      }
    },
    [enableOrdering, logger, query]
  );

  const isRestaurantAvailable = useCallback(
    async (restaurantData: IStore | StoreState) => {
      const mobileOrderingEnabled = isMobileOrderingAvailable(restaurantData as IStore);
      const { number: storeId } = restaurantData as IStore;

      const isRestaurantPosAvailable = await checkIfRestaurantPosIsOnline(storeId);

      const open =
        !serviceMode ||
        [ServiceMode.DELIVERY, ServiceMode.CATERING_PICKUP, ServiceMode.CATERING_DELIVERY].includes(
          serviceMode
        ) ||
        (serviceModeStatus[serviceMode].available && !serviceModeStatus[serviceMode].disabled);

      return open && isRestaurantPosAvailable && mobileOrderingEnabled;
    },
    [checkIfRestaurantPosIsOnline, serviceMode, serviceModeStatus]
  );

  return {
    serviceModeStatus,
    allServiceModesUnavailable,
    checkIfRestaurantPosIsOnline,
    isRestaurantAvailable
  };
};
